import { doc, getDoc } from 'firebase/firestore'; // Import Firestore functions
import { jsPDF } from 'jspdf'; // Import jsPDF library
import * as XLSX from 'xlsx';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom'; // To get the order ID from URL
import LoadingSpinner from '../assets/LoadingSpinner';
import '../css/ViewOrderReceipt.css';
import { db } from '../firebase-config'; // Import db from firebase-config

const ViewOrderReceipt = () => {
  const navigate = useNavigate()
  const { id } = useParams();  // Get the order ID from the URL
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch order details from Firestore
    const fetchOrderDetails = async () => {
      try {
        const orderRef = doc(db, 'orderReceipts', id);  // Reference to the Firestore document
        const orderDoc = await getDoc(orderRef);

        if (orderDoc.exists()) {
          setOrder(orderDoc.data()); // Set the order data to state if it exists
        } else {
          setError('Order not found.');
        }
        setLoading(false);  // Stop loading after the request
      } catch (err) {
        setError('Error fetching order details.');
        console.error(err); // Log the error in case of failure
        setLoading(false);
      }
    };

    fetchOrderDetails();  // Call the function to fetch the order details
  }, [id]);  // Only re-run the effect if the `id` changes (e.g., different order ID)

  // Loading and error states
  if (loading) return <LoadingSpinner isVisible={true} />;
  if (error) return <div>Error: {error}</div>;

  const calculateXOffset = (textWidth) => {
    return 77 + (textWidth / 2);
  };

  // Function to generate Payment Receipt PDF
  const generatePaymentReceiptExcel = () => {
  
    // 3. Create a summary sheet
    const summarySheetData = [
      [" "],
      ["Branch Name", order.branch],
      ["Delivery Date", order.deliveryDate],
      ["Date Created", order.date],
      ["User", order.lastUpdatedBy.split('@')[0]],
      [" "],
      ["PRODUCTS/INGREDIENTS", "QUANTITY", "PRICE", "TOTAL"],
    ];

    order.ingredients.forEach(item => {
      if (item.item) {
        summarySheetData.push([
          item.item,
          item.quantity,
          'PHP ' + item.price,
          'PHP ' + item.totalIngredientPrice
        ]);
      }
    });

    order.items.forEach(item => {
      if (item.item) {
        summarySheetData.push([
          item.item,
          item.quantity,
          'PHP ' + item.price,
          'PHP ' + item.totalItemPrice
        ]);
      }
    });

    summarySheetData.push([""])
    summarySheetData.push(["", "", "Subtotal", `PHP ${order.subtotal}`])
    summarySheetData.push(["", "", "Shipping Fee", `PHP ${order.shippingFee}`])
    summarySheetData.push(["", "", "Final Total Price", `PHP ${order.totalAmount}`])
  
    // 4. Convert to Excel Sheets
    const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
    XLSX.utils.sheet_add_aoa(summarySheet, [["Dimsum Treats", " ", " ", " "]], { origin: "A1" });

    summarySheet["!cols"] = [
      { wch: 40 },
      { wch: 20 },
      { wch: 20 },
      { wch: 20 }
    ];
  
    // 5. Create Workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
  
    // 6. Save Excel File
    XLSX.writeFile(workbook, 'payment_receipt.xlsx');
  };

  // Function to generate Delivery Receipt PDF (without prices)
  const generateDeliveryReceiptExcel = () => {
  
      // 3. Create a summary sheet
      const summarySheetData = [
        [" "],
        ["Branch Name", order.branch],
        ["Delivery Date", order.deliveryDate],
        ["Date Created", order.date],
        ["User", order.lastUpdatedBy.split('@')[0]],
        [" "],
        ["PRODUCTS/INGREDIENTS", "QUANTITY"],
      ];
  
      order.ingredients.forEach(item => {
        if (item.item) {
          summarySheetData.push([
            item.item,
            item.quantity,
          ]);
        }
      });
  
      order.items.forEach(item => {
        if (item.item) {
          summarySheetData.push([
            item.item,
            item.quantity,
          ]);
        }
      });
    
      // 4. Convert to Excel Sheets
      const summarySheet = XLSX.utils.aoa_to_sheet(summarySheetData);
      XLSX.utils.sheet_add_aoa(summarySheet, [["Dimsum Treats", " ", " ", " "]], { origin: "A1" });
  
      summarySheet["!cols"] = [
        { wch: 40 },
        { wch: 20 },
      ];
    
      // 5. Create Workbook
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");
    
      // 6. Save Excel File
      XLSX.writeFile(workbook, 'delivery_receipt.xlsx');
    };

  return (
    <div className="order-details-container">
      <h1>Order Receipt Details</h1>
      <div className="order-details">
        {order && order.branch && (
          <>
            <p><strong>Branch:</strong> {order.branch}</p>
            <p><strong>Delivery Date:</strong> {order.deliveryDate}</p>
            <p><strong>Date Created:</strong> {new Date(order.date).toLocaleString('en-US', {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
              })}</p>
            <p><strong>User Email:</strong> {order.lastUpdatedBy || 'Hacker'}</p>

            <h3>Ingredients:</h3>
            <ul className="ul-vieworder">
            {Array.isArray(order.ingredients) || order.ingredients.length > 0 ? (
                order.ingredients.map((ingredient, index) => (
                  <li key={index} className="li-vieworder">
                    {ingredient.item} - {ingredient.quantity} {ingredient.measurement} - - - - - - PHP {ingredient.totalIngredientPrice}
                  </li>
                ))
              ) : (
                <li>No ingredients available</li>
              )}
            </ul>
            <h3>Products:</h3>
            <ul className="ul-vieworder">
              {Array.isArray(order.items) ? (
                order.items.map((item, index) => (
                  <li key={index} className="li-vieworder">
                    {item.item} - {item.quantity} {item.measurement} - - - - - - PHP {item.totalItemPrice}
                  </li>
                ))
              ) : (
                <li>No Products available</li>
              )}
            </ul>
          </>
        )}
            <p><strong>Subtotal:</strong> ₱{order.subtotal || '0'}</p>
            <p><strong>Shipping Fee:</strong> ₱{order.shippingFee || '0'}</p>
            <p><strong>Total Amount:</strong> ₱{order.totalAmount || '0'}</p>
      </div>

      {/* Action buttons for PDF generation */}
      <div className="pdf-buttons">
        <button onClick={generatePaymentReceiptExcel} className="generate-pdf-btn">
          Print Payment Receipt
        </button>
        <button onClick={generateDeliveryReceiptExcel} className="generate-pdf-btn">
          Print Delivery Receipt
        </button>
      </div>
      <button className="cancel-btn-orderhistory" onClick={() => navigate('/pages/OrderHistory')}>
        Done
      </button>

    </div>
  );
};

export default ViewOrderReceipt;